import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const statutColors = { 
  en_attente: "warning", 
  confirmee: "success", 
  refusee: "error",
  archivee: "default",
};

const statutLabels = { 
  en_attente: "En attente", 
  confirmee: "Confirmée", 
  refusee: "Refusée",
  archivee: "Archivée",
};

function StatCard({ icon, label, value, color, iconColor }) {
  return (
    <Card sx={{ p: 3, height: "100%" }}>
      <MDBox display="flex" alignItems="center" gap={2}>
        <MDBox
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            bgcolor: color || "#e3f2fd",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: iconColor || "#1565c0", fontSize: 30 }}>{icon}</Icon>
        </MDBox>
        <MDBox>
          <MDTypography variant="body2" fontWeight="medium" color="text" display="block">
            {label}
          </MDTypography>
          <MDTypography variant="h4" fontWeight="bold" sx={{ lineHeight: 1.2, color: iconColor }}>
            {value}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
}

function BarChart({ data, color }) {
  const max = Math.max(...data.map((d) => parseInt(d.count)), 1);
  return (
    <MDBox>
      {data.map((d, i) => (
        <MDBox key={i} mb={2}>
          <MDBox display="flex" justifyContent="space-between" mb={0.75}>
            <MDTypography variant="body2" fontWeight="medium">
              {d.nom}
            </MDTypography>
            <MDTypography variant="body2" fontWeight="bold" sx={{ color }}>
              {d.count}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              width: "100%",
              height: 10,
              bgcolor: "#f0f0f0",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <MDBox
              sx={{
                width: `${(parseInt(d.count) / max) * 100}%`,
                height: "100%",
                bgcolor: color || "#1976d2",
                borderRadius: 4,
                transition: "width 0.8s ease",
              }}
            />
          </MDBox>
        </MDBox>
      ))}
      {data.length === 0 && (
        <MDTypography variant="body2" color="text">
          Aucune donnée disponible.
        </MDTypography>
      )}
    </MDBox>
  );
}

function formatMontant(value) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    Number(value) || 0
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtreRetours, setFiltreRetours] = useState("");

  useEffect(() => {
    api
      .get("/reservations/stats")
      .then((res) => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const editionRetours =
    filtreRetours !== ""
      ? (stats?.retours_par_edition || []).find((e) => e.id === filtreRetours)
      : null;
  const retours = editionRetours
    ? {
        participants: editionRetours.total_participants_reels,
        estimes: editionRetours.total_participants_estimes,
        nouveaux: editionRetours.total_nouveaux,
        nbFeedbacks: editionRetours.nb_feedbacks,
        cout: editionRetours.total_cout,
        recettes: editionRetours.total_recettes,
        benefice: editionRetours.benefice,
      }
    : {
        participants: stats?.total_participants_reels || 0,
        estimes: stats?.total_participants_estimes || 0,
        nouveaux: stats?.total_nouveaux || 0,
        nbFeedbacks: stats?.nb_feedbacks || 0,
        cout: stats?.total_cout || 0,
        recettes: stats?.total_recettes || 0,
        benefice: stats?.benefice_total || 0,
      };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} display="flex" justifyContent="center">
          <MDTypography variant="h6" color="text">
            Chargement...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Titre */}
        <MDTypography variant="h5" fontWeight="bold" mb={3}>
          Tableau de bord
        </MDTypography>

        {/* Statistiques globales */}
        <MDTypography variant="subtitle2" fontWeight="medium" color="text" mb={2}>
          Statistiques globales
        </MDTypography>
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={3}>
            <StatCard
              icon="event_note"
              label="Total réservations"
              value={stats?.total_reservations || 0}
              color="#e3f2fd"
              iconColor="#1565c0"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon="hourglass_empty"
              label="En attente"
              value={stats?.par_statut?.en_attente || 0}
              color="#fff3e0"
              iconColor="#e65100"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon="check_circle"
              label="Confirmées"
              value={stats?.par_statut?.confirmee || 0}
              color="#e8f5e9"
              iconColor="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon="cancel"
              label="Refusées"
              value={stats?.par_statut?.refusee || 0}
              color="#ffebee"
              iconColor="#c62828"
            />
          </Grid>
        </Grid>

        {/* Répartition par statut et par édition */}
        <Grid container spacing={3} mb={4}>
          {/* Répartition par statut */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: "100%" }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={3}>
                <Icon sx={{ color: "#1976d2", fontSize: 22 }}>pie_chart</Icon>
                <MDTypography variant="h6" fontWeight="bold">
                  Répartition par statut
                </MDTypography>
              </MDBox>
              <MDBox>
                {Object.entries(statutLabels).map(([key, label]) => {
                  const count = stats?.par_statut?.[key] || 0;
                  const total = stats?.total_reservations || 1;
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <MDBox key={key} mb={2.5}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.75}
                      >
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDBox
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: statutColors[key],
                            }}
                          />
                          <MDTypography variant="body2" fontWeight="medium">
                            {label}
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDTypography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ color: statutColors[key] }}
                          >
                            {count}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            ({pct}%)
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: "100%",
                          height: 10,
                          bgcolor: "#f0f0f0",
                          borderRadius: 4,
                          overflow: "hidden",
                        }}
                      >
                        <MDBox
                          sx={{
                            width: `${pct}%`,
                            height: "100%",
                            bgcolor: statutColors[key],
                            borderRadius: 4,
                            transition: "width 0.8s ease",
                          }}
                        />
                      </MDBox>
                    </MDBox>
                  );
                })}
              </MDBox>
            </Card>
          </Grid>

          {/* Répartition par édition */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: "100%" }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={3}>
                <Icon sx={{ color: "#7b1fa2", fontSize: 22 }}>calendar_today</Icon>
                <MDTypography variant="h6" fontWeight="bold">
                  Répartition par édition
                </MDTypography>
              </MDBox>
              <MDBox>
                {stats?.par_edition?.length > 0 ? (
                  stats.par_edition.map((e, i) => {
                    const max = Math.max(
                      ...stats.par_edition.map((x) => parseInt(x.total)),
                      1
                    );
                    const pct = Math.round((parseInt(e.total) / max) * 100);
                    return (
                      <MDBox key={i} mb={3}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={0.75}
                        >
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={e.active ? "Active" : "Archivée"}
                              size="small"
                              sx={{
                                bgcolor: e.active ? "#e8f5e9" : "#f5f5f5",
                                color: e.active ? "#2e7d32" : "#757575",
                                fontSize: 11,
                                fontWeight: "bold",
                                height: 22,
                              }}
                            />
                            <MDTypography variant="body2" fontWeight="bold">
                              {e.libelle}
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <MDTypography
                              variant="h6"
                              fontWeight="bold"
                              sx={{ color: "#7b1fa2" }}
                            >
                              {e.total}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              réservation(s)
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <MDBox
                          sx={{
                            width: "100%",
                            height: 10,
                            bgcolor: "#f0f0f0",
                            borderRadius: 4,
                            overflow: "hidden",
                          }}
                        >
                          <MDBox
                            sx={{
                              width: `${pct}%`,
                              height: "100%",
                              bgcolor: e.active ? "#7b1fa2" : "#9e9e9e",
                              borderRadius: 4,
                              transition: "width 0.8s ease",
                            }}
                          />
                        </MDBox>
                        <MDBox display="flex" gap={3} mt={1}>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <MDBox
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#fb8c00",
                              }}
                            />
                            <MDTypography
                              variant="body2"
                              fontWeight="medium"
                              sx={{ color: "#e65100" }}
                            >
                              {e.en_attente} en attente
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <MDBox
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#43a047",
                              }}
                            />
                            <MDTypography
                              variant="body2"
                              fontWeight="medium"
                              sx={{ color: "#2e7d32" }}
                            >
                              {e.confirmee} confirmées
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <MDBox
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#e53935",
                              }}
                            />
                            <MDTypography
                              variant="body2"
                              fontWeight="medium"
                              sx={{ color: "#c62828" }}
                            >
                              {e.refusee} refusées
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    );
                  })
                ) : (
                  <MDTypography variant="body2" color="text">
                    Aucune édition créée.
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Groupes */}
        <MDTypography variant="subtitle2" fontWeight="medium" color="text" mb={2}>
          Groupes de chants
        </MDTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard
              icon="music_note"
              label="Nombre Total de Groupes"
              value={stats?.total_groupes || 0}
              color="#f3e5f5"
              iconColor="#6a1b9a"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <Icon sx={{ color: "#1976d2", fontSize: 22 }}>trending_up</Icon>
                <MDTypography variant="h6" fontWeight="bold">
                  Plus demandés
                </MDTypography>
              </MDBox>
              <BarChart data={stats?.groupes_demandes || []} color="#1976d2" />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <Icon sx={{ color: "#43a047", fontSize: 22 }}>star</Icon>
                <MDTypography variant="h6" fontWeight="bold">
                  Plus affectés
                </MDTypography>
              </MDBox>
              <BarChart data={stats?.groupes_affectes || []} color="#43a047" />
            </Card>
          </Grid>
        </Grid>

        {/* Retours de fêtes */}
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mt={4}
          mb={2}
        >
          <MDTypography variant="subtitle2" fontWeight="medium" color="text">
            📊 Retours de fêtes
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <MDTypography variant="caption" fontWeight="medium" color="text">
              Édition :
            </MDTypography>
            <Chip
              label="Toutes"
              onClick={() => setFiltreRetours("")}
              size="small"
              sx={{
                cursor: "pointer",
                bgcolor: filtreRetours === "" ? "#1976d2" : "transparent",
                color: filtreRetours === "" ? "white" : "text.primary",
                border: "1px solid #1976d2",
                fontWeight: filtreRetours === "" ? "bold" : "normal",
              }}
            />
            {(stats?.retours_par_edition || []).map((e) => (
              <Chip
                key={e.id}
                size="small"
                onClick={() => setFiltreRetours(e.id)}
                label={
                  <MDBox component="span" display="flex" alignItems="center" gap={0.75}>
                    {e.libelle}
                    {!e.active && (
                      <MDBox
                        component="span"
                        sx={{
                          bgcolor: filtreRetours === e.id ? "rgba(255,255,255,0.25)" : "#eeeeee",
                          color: filtreRetours === e.id ? "white" : "#757575",
                          fontSize: 10,
                          fontWeight: "bold",
                          px: 0.75,
                          borderRadius: 1,
                        }}
                      >
                        Archivée
                      </MDBox>
                    )}
                  </MDBox>
                }
                sx={{
                  cursor: "pointer",
                  bgcolor:
                    filtreRetours === e.id ? (e.active ? "#43a047" : "#757575") : "transparent",
                  color: filtreRetours === e.id ? "white" : "text.primary",
                  border: `1px solid ${e.active ? "#43a047" : "#757575"}`,
                  fontWeight: filtreRetours === e.id ? "bold" : "normal",
                }}
              />
            ))}
          </MDBox>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: "100%" }}>
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDBox
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    bgcolor: "#e0f2f1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ color: "#00695c", fontSize: 30 }}>groups</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant="body2" fontWeight="medium" color="text" display="block">
                    Total participants réels
                  </MDTypography>
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ lineHeight: 1.2, color: "#00695c" }}
                  >
                    {retours.participants}
                  </MDTypography>
                  {retours.estimes > 0 && (
                    <MDTypography variant="caption" color="text">
                      sur {retours.estimes} estimés (
                      {retours.participants >= retours.estimes ? "+" : ""}
                      {Math.round(((retours.participants - retours.estimes) / retours.estimes) * 100)}
                      %)
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              icon="person_add"
              label="Dont nouveaux participants"
              value={retours.nouveaux}
              color="#e8eaf6"
              iconColor="#283593"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              icon="rate_review"
              label="Retours soumis"
              value={retours.nbFeedbacks}
              color="#fff8e1"
              iconColor="#ef6c00"
            />
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={3}>
                <Icon sx={{ color: "#f9a825", fontSize: 22 }}>account_balance_wallet</Icon>
                <MDTypography variant="h6" fontWeight="bold">
                  Bilan financier
                </MDTypography>
                {editionRetours && (
                  <Chip
                    label={editionRetours.active ? editionRetours.libelle : `${editionRetours.libelle} · Archivée`}
                    size="small"
                    sx={{
                      bgcolor: editionRetours.active ? "#e8f5e9" : "#f5f5f5",
                      color: editionRetours.active ? "#2e7d32" : "#757575",
                      fontSize: 11,
                      fontWeight: "bold",
                      height: 22,
                    }}
                  />
                )}
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <MDTypography variant="body2" fontWeight="medium" color="text" display="block">
                    Coût total
                  </MDTypography>
                  <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#c62828" }}>
                    {formatMontant(retours.cout)}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDTypography variant="body2" fontWeight="medium" color="text" display="block">
                    Recettes totales
                  </MDTypography>
                  <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1565c0" }}>
                    {formatMontant(retours.recettes)}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDTypography variant="body2" fontWeight="medium" color="text" display="block">
                    {retours.benefice >= 0 ? "Bénéfice" : "Déficit"}
                  </MDTypography>
                  <MDTypography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: retours.benefice >= 0 ? "#2e7d32" : "#c62828" }}
                  >
                    {retours.benefice >= 0 ? "+" : "−"}
                    {formatMontant(Math.abs(retours.benefice))}
                  </MDTypography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;